import React from 'react';
import { Megaphone, MapPin, Calendar, Coins, CheckCircle2, XCircle } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { SALON_TIERS } from '../../store/slices/marketingSalonSlice';

/**
 * MarketingSalonWidget — Dashboard panel for the marketing salon narrative arc.
 *
 * 05/02/2026 (Q1 Lot 2) — Affichage conditionnel :
 *   • Pending invitation : 4 boutons budget (Skip / Visiteur / Standard / Premium)
 *   • Active salon       : carte "en cours" avec compte à rebours
 *   • Idle               : pas de rendu (le widget se cache)
 */
export default function MarketingSalonWidget() {
  const invitation = useGameStore(s => s.pendingSalonInvitation);
  const activeSalon = useGameStore(s => s.marketingSalon);
  const totalDays = useGameStore(s => s.totalDays || 0);
  const treasury = useGameStore(s => s.treasury || 0);
  const attendSalon = useGameStore(s => s.attendMarketingSalon);
  const declineSalon = useGameStore(s => s.declineMarketingSalon);

  // Active salon takes priority over pending invitation.
  if (activeSalon && activeSalon.active) {
    const daysLeft = Math.max(0, activeSalon.resultDay - totalDays);
    const tierData = SALON_TIERS[activeSalon.tier];
    return (
      <div
        className="surface-card p-4 border-l-4 border-pink-500"
        data-testid="marketing-salon-active"
      >
        <div className="flex items-center gap-2 mb-2">
          <Megaphone className="w-4 h-4 text-pink-300" />
          <h3 className="text-sm font-bold text-pink-200 uppercase tracking-wider">
            Salon en cours — {activeSalon.eventName/* i18n-keep — narrative FR */}
          </h3>
        </div>
        <p className="text-xs text-slate-400 mb-2">
          <MapPin className="inline w-3 h-3 mr-1" />
          {activeSalon.city/* i18n-keep — city name */} ·
          <Calendar className="inline w-3 h-3 mx-1" />
          Bilan dans <strong className="text-pink-300">{daysLeft} jour(s)</strong>
        </p>
        <p className="text-xs text-slate-500">
          Niveau : <strong className="text-slate-300">{tierData?.label || activeSalon.tier/* i18n-keep */}</strong> ·
          Budget engagé : <strong className="text-slate-300">{activeSalon.tierCost.toLocaleString('fr-FR')} €</strong>
        </p>
      </div>
    );
  }

  if (!invitation || !invitation.active) return null;

  const daysToExpire = Math.max(0, (invitation.expiresOnDay || 0) - totalDays);

  const tierBtns = [
    {
      key: 'visit',
      ...SALON_TIERS.visit,
      tone: 'bg-slate-700/40 border-slate-500/40 text-slate-200 hover:bg-slate-600/40',
    },
    {
      key: 'standard',
      ...SALON_TIERS.standard,
      tone: 'bg-sky-700/30 border-sky-500/40 text-sky-100 hover:bg-sky-600/40',
    },
    {
      key: 'premium',
      ...SALON_TIERS.premium,
      tone: 'bg-amber-700/30 border-amber-500/50 text-amber-100 hover:bg-amber-600/40',
    },
  ];

  const handleAttend = (tier) => {
    const res = attendSalon(tier);
    if (!res?.ok) {
      // eslint-disable-next-line no-console
      console.warn('[salon] attend failed:', res?.reason);
    }
  };

  return (
    <div
      className="surface-card p-4 border-l-4 border-pink-500"
      data-testid="marketing-salon-invitation"
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Megaphone className="w-4 h-4 text-pink-300" />
          <h3 className="text-sm font-bold text-pink-200 uppercase tracking-wider">
            Invitation salon — {invitation.eventName/* i18n-keep — narrative FR */}
          </h3>
        </div>
        <span className="text-[10px] uppercase tracking-widest font-mono text-slate-400">
          <Calendar className="inline w-3 h-3 mr-1" />
          {daysToExpire}j restants{/* i18n-keep */}
        </span>
      </div>
      <p className="text-xs text-slate-400 mb-3">
        <MapPin className="inline w-3 h-3 mr-1" />
        {invitation.city/* i18n-keep */} · Choisissez votre niveau d'engagement (ou passez votre tour).
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
        {tierBtns.map(t => {
          const affordable = treasury >= t.cost;
          return (
            <button
              type="button"
              key={t.key}
              disabled={!affordable}
              onClick={() => handleAttend(t.key)}
              className={`text-left p-3 rounded border ${t.tone} disabled:opacity-40 disabled:cursor-not-allowed`}
              data-testid={`salon-tier-${t.key}`}
            >
              <p className="font-bold text-sm flex items-center justify-between">
                <span>{t.label/* i18n-keep — FR label */}</span>
                <Coins className="w-3 h-3 opacity-60" />
              </p>
              <p className="text-[11px] font-mono mt-0.5">
                {t.cost.toLocaleString('fr-FR')} €
              </p>
              <p className="text-[10px] mt-1 leading-tight">
                +{t.notoriety} notoriété
                {t.reputation > 0 && <> · +{t.reputation} rép.</>}
              </p>
              {t.prospects > 0 && (
                <p className="text-[10px] mt-0.5 italic">
                  +{t.prospects} prospect{t.prospects > 1 ? 's' : ''} AO{/* i18n-keep */}
                </p>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-2">
        <p className="text-[10px] text-slate-500 italic">
          Le bilan tombe 7 jours après inscription.{/* i18n-keep */}
        </p>
        <button
          type="button"
          onClick={() => declineSalon()}
          className="text-[11px] text-slate-400 hover:text-slate-200 inline-flex items-center gap-1"
          data-testid="salon-decline"
        >
          <XCircle className="w-3 h-3" />
          Passer notre tour{/* i18n-keep */}
        </button>
      </div>
    </div>
  );
}

// Expose icon for any external consumers.
export { CheckCircle2 };
